import { locales, type Locale } from "@/lib/i18n/config";
import { KIND_ROUTE_SLUG } from "@/lib/routing";
import type { ItemKind } from "@/lib/data";
import { CANONICAL_ITEMS } from "../../../../../src/data/generated/items";
import { STATIC_PAGE_COPY_ID } from "@/lib/seo/page-copy.id";
import { STATIC_PAGE_COPY_TH } from "@/lib/seo/page-copy.th";
import { STATIC_PAGE_COPY_DE } from "@/lib/seo/page-copy.de";

/**
 * SEO title/description copy for the static and index routes, per locale.
 *
 * **Shared on purpose.** This used to live inside scripts/generate-static-meta.ts while each page
 * component passed its own short heading to useHeadTags, so the prerendered HTML said "All Fish in
 * Heartopia — Complete List" and, milliseconds after hydration, document.title became "Catch Every
 * Fish". Measured on 2026-08-04: all 12 index routes disagreed, in title and description both.
 * Google renders JS, so the version it indexed was the component's — every line of copy written
 * here for the search result was being thrown away.
 *
 * Anything that sets a <title> for these routes reads it from here: the build script by relative
 * import, the components through "@/lib/seo/page-copy".
 */
/** Copy table for the static/index routes that used to ship the raw SPA shell (homepage title +
 * description on every one of them — the single biggest bucket in Bing's duplicate-meta report).
 * Mirrors generate-sitemap.ts's STATIC_ENTRIES; trust pages are covered by trustPageMetas() and
 * per-kind catalog pages are generated below from KIND_PLURAL_L10N + live item counts. */
export type LocaleCopy = Record<Locale, { title: string; description: string }>;

/** Plural kind labels for catalog index and tracker pages. Moved here from
 * generate-static-meta.ts along with the copy that uses them; also used by that script's
 * location descriptions ("14 fish, 3 forageables"). */
export const KIND_PLURAL_L10N: Record<Locale, Record<ItemKind, string>> = {
  en: {
    fish: "Fish",
    insect: "Insects",
    bird: "Birds",
    crop: "Crops",
    flower: "Flowers",
    forageable: "Forageables",
    animal: "Animals",
    dish: "Recipes",
    ingredient: "Ingredients",
    material: "Materials",
    seed: "Seeds",
    vehicle: "Vehicles",
    furniture: "Furniture",
    tool: "Tools",
    clothes: "Clothes",
    special: "Special Items",
  },
  "pt-BR": {
    fish: "Peixes",
    insect: "Insetos",
    bird: "Pássaros",
    crop: "Cultivos",
    flower: "Flores",
    forageable: "Coletáveis",
    animal: "Animais",
    dish: "Receitas",
    ingredient: "Ingredientes",
    material: "Materiais",
    seed: "Sementes",
    vehicle: "Veículos",
    furniture: "Móveis",
    tool: "Ferramentas",
    clothes: "Roupas",
    special: "Itens Especiais",
  },
  es: {
    fish: "Peces",
    insect: "Insectos",
    bird: "Aves",
    crop: "Cultivos",
    flower: "Flores",
    forageable: "Recolectables",
    animal: "Animales",
    dish: "Recetas",
    ingredient: "Ingredientes",
    material: "Materiales",
    seed: "Semillas",
    vehicle: "Vehículos",
    furniture: "Muebles",
    tool: "Herramientas",
    clothes: "Ropa",
    special: "Objetos Especiales",
  },
  id: {
    fish: "Ikan",
    insect: "Serangga",
    bird: "Burung",
    crop: "Tanaman",
    flower: "Bunga",
    forageable: "Hasil Alam",
    animal: "Hewan",
    dish: "Resep",
    ingredient: "Bahan",
    material: "Material",
    seed: "Benih",
    vehicle: "Kendaraan",
    furniture: "Furnitur",
    tool: "Alat",
    clothes: "Pakaian",
    special: "Item Khusus",
  },
  th: {
    fish: "ปลา",
    insect: "แมลง",
    bird: "นก",
    crop: "พืชผล",
    flower: "ดอกไม้",
    forageable: "ของเก็บได้",
    animal: "สัตว์",
    dish: "สูตรอาหาร",
    ingredient: "วัตถุดิบ",
    material: "วัสดุ",
    seed: "เมล็ดพันธุ์",
    vehicle: "ยานพาหนะ",
    furniture: "เฟอร์นิเจอร์",
    tool: "เครื่องมือ",
    clothes: "เสื้อผ้า",
    special: "ไอเทมพิเศษ",
  },
  de: {
    fish: "Fische",
    insect: "Insekten",
    bird: "Vögel",
    crop: "Feldfrüchte",
    flower: "Blumen",
    forageable: "Sammelgut",
    animal: "Tiere",
    dish: "Rezepte",
    ingredient: "Zutaten",
    material: "Materialien",
    seed: "Samen",
    vehicle: "Fahrzeuge",
    furniture: "Möbel",
    tool: "Werkzeuge",
    clothes: "Kleidung",
    special: "Spezialgegenstände",
  },
};

/** Mirrors tracker-routing.ts's TRACKER_CATEGORY_SLUG, as slug -> kind. */
const TRACKER_CATEGORY_KINDS: Record<string, ItemKind> = {
  fish: "fish",
  insects: "insect",
  birds: "bird",
  crops: "crop",
  flowers: "flower",
  forageables: "forageable",
  recipes: "dish",
  animals: "animal",
};

function itemCount(kind: ItemKind): number {
  return CANONICAL_ITEMS.filter((item) => item.kind === kind && item.status !== "unreleased").length;
}

/**
 * Catalog index + tracker category copy.
 *
 * pt-BR and es deliberately use different sentence shapes ("Lista Completa" vs "Guía Completa",
 * "de Heartopia" vs "en Heartopia"). The two languages produce the same words for several kinds —
 * Cultivos, Flores, Ingredientes are spelled identically — so a shared template made
 * /pt-br/flowers and /es/flowers ship byte-identical titles. Localising the descriptor cannot fix
 * that; only phrasing the two differently can.
 */
function generatedIndexCopy(): Record<string, LocaleCopy> {
  const out: Record<string, LocaleCopy> = {};

  for (const kind of Object.keys(KIND_ROUTE_SLUG) as ItemKind[]) {
    const count = itemCount(kind);
    const copy = {} as LocaleCopy;
    for (const locale of locales) {
      const label = KIND_PLURAL_L10N[locale][kind];
      // "habitats/seasons" copy fits creatures and plants; dishes get cooking-specific wording.
      if (locale === "pt-BR") {
        copy[locale] = {
          title: `${label} de Heartopia — Lista Completa | Heartopia.guide`,
          description:
            kind === "dish"
              ? `Lista completa com ${count} receitas de Heartopia: ingredientes, nível de culinária necessário e preço de venda de cada prato, com busca e filtros.`
              : `Lista completa com ${count} ${label.toLowerCase()} de Heartopia: preços de venda, habitats, estações e locais exatos, com busca e filtros.`,
        };
      } else if (locale === "es") {
        copy[locale] = {
          title: `${label} en Heartopia — Guía Completa | Heartopia.guide`,
          description:
            kind === "dish"
              ? `Guía completa con ${count} recetas de Heartopia: ingredientes, nivel de cocina necesario y precio de venta de cada plato, con búsqueda y filtros.`
              : `Guía completa con ${count} ${label.toLowerCase()} en Heartopia: precios de venta, hábitats, temporadas y ubicaciones exactas, con búsqueda y filtros.`,
        };
      } else if (locale === "id") {
        copy[locale] = {
          title: `Semua ${label} di Heartopia — Daftar Lengkap | Heartopia.guide`,
          description:
            kind === "dish"
              ? `Jelajahi ${count} resep Heartopia: bahan, level Cooking yang diperlukan, dan harga jual setiap hidangan, lengkap dengan pencarian dan filter.`
              : `Jelajahi ${count} ${label.toLowerCase()} di Heartopia beserta harga jual, habitat, musim, dan lokasi tepatnya dalam daftar lengkap yang bisa dicari.`,
        };
      } else if (locale === "th") {
        copy[locale] = {
          title: `${label}ทั้งหมดใน Heartopia — รายการครบถ้วน | Heartopia.guide`,
          description:
            kind === "dish"
              ? `ดูสูตรอาหาร Heartopia ทั้ง ${count} สูตร พร้อมวัตถุดิบ ระดับทำอาหารที่ต้องใช้ และราคาขายของแต่ละเมนู รวมทั้งระบบค้นหาและตัวกรอง`
              : `ดู${label}ทั้ง ${count} รายการใน Heartopia พร้อมราคาขาย ถิ่นอาศัย ฤดูกาล และตำแหน่งที่แน่นอนในรายการที่ค้นหาได้`,
        };
      } else if (locale === "de") {
        copy[locale] = {
          title: `Alle ${label} in Heartopia — komplette Liste | Heartopia.guide`,
          description:
            kind === "dish"
              ? `Alle ${count} Rezepte in Heartopia: Zutaten, benötigte Cooking-Stufe und Verkaufspreis für jedes Gericht — durchsuchbar und filterbar.`
              : `Alle ${count} ${label} in Heartopia mit Verkaufspreisen, Lebensräumen, Jahreszeiten und genauen Fundorten — eine vollständige, durchsuchbare Liste.`,
        };
      } else {
        copy[locale] = {
          title: `All ${label} in Heartopia — Complete List | Heartopia.guide`,
          description:
            kind === "dish"
              ? `Browse all ${count} Heartopia cooking recipes: ingredients, required cooking level and sell price for every dish, searchable and filterable.`
              : `Browse all ${count} ${label.toLowerCase()} in Heartopia with sell prices, habitats, seasons and exact locations — a complete, searchable list.`,
        };
      }
    }
    out[`/${KIND_ROUTE_SLUG[kind]}`] = copy;
  }

  for (const [slug, kind] of Object.entries(TRACKER_CATEGORY_KINDS)) {
    const copy = {} as LocaleCopy;
    for (const locale of locales) {
      const label = KIND_PLURAL_L10N[locale][kind];
      if (locale === "pt-BR") {
        copy[locale] = {
          title: `Rastreador de ${label} de Heartopia | Heartopia.guide`,
          description: `Marque cada um dos ${label.toLowerCase()} de Heartopia que você já coletou e acompanhe quanto falta para completar a coleção.`,
        };
      } else if (locale === "es") {
        copy[locale] = {
          title: `Rastreador de ${label} en Heartopia | Heartopia.guide`,
          description: `Marca cada uno de los ${label.toLowerCase()} en Heartopia que ya conseguiste y sigue cuánto te falta para completar la colección.`,
        };
      } else if (locale === "id") {
        copy[locale] = {
          title: `Pelacak ${label} Heartopia | Heartopia.guide`,
          description: `Tandai setiap ${label.toLowerCase()} Heartopia yang sudah kamu kumpulkan dan lihat progresmu menuju koleksi lengkap.`,
        };
      } else if (locale === "th") {
        copy[locale] = {
          title: `ตัวติดตาม${label} Heartopia | Heartopia.guide`,
          description: `ทำเครื่องหมาย${label} Heartopia ที่คุณเก็บได้แล้วและดูความคืบหน้าสู่การสะสมให้ครบทั้งหมด`,
        };
      } else {
        copy[locale] = {
          title: `Heartopia ${label} Tracker | Heartopia.guide`,
          description: `Check off every Heartopia ${label.toLowerCase().replace(/s$/, "")} you've collected and see exactly how far you are from completing the set.`,
        };
      }
    }
    out[`/tracker/${slug}`] = copy;
  }

  return out;
}

/** Every route this module has copy for: the hand-written table plus the generated catalog and
 * tracker entries. This is what useHeadTags and generate-static-meta.ts both look up. */
export function pageCopyFor(path: string, locale: Locale): { title: string; description: string } | undefined {
  return (STATIC_PAGE_COPY[path] ?? GENERATED_INDEX_COPY[path])?.[locale];
}

/** The generated (catalog + tracker) paths, for generate-static-meta.ts to iterate when emitting
 * their PageMeta entries. The hand-written STATIC_PAGE_COPY keys are iterated separately there. */
export function indexCopyPaths(): string[] {
  return Object.keys(GENERATED_INDEX_COPY);
}

const STATIC_PAGE_COPY_BASE: Record<string, Omit<LocaleCopy, "id" | "th" | "de">> = {
  "/": {
    en: {
      title: "Heartopia.guide — Island Atlas, Recipes, Map & Collection Tracker",
      description:
        "A player-made Heartopia field guide: interactive island map, fish, insects, birds, crops, cooking recipes, NPCs, gift codes and a full collection tracker.",
    },
    "pt-BR": {
      title: "Heartopia.guide — Atlas da Ilha, Receitas, Mapa e Rastreador de Coleção",
      description:
        "Um guia de Heartopia feito por jogadores: mapa interativo da ilha, peixes, insetos, pássaros, cultivos, receitas, NPCs, códigos e rastreador de coleção completo.",
    },
    es: {
      title: "Heartopia.guide — Atlas de la Isla, Recetas, Mapa y Rastreador de Colección",
      description:
        "Una guía de Heartopia hecha por jugadores: mapa interactivo de la isla, peces, insectos, aves, cultivos, recetas, NPC, códigos y rastreador de colección completo.",
    },
  },
  "/map": {
    en: {
      title: "Interactive Heartopia Island Map — All Locations | Heartopia.guide",
      description:
        "Explore the full Heartopia island map: every location, fishing area, NPC and shop, with filters for fish spots, foraging areas and collectibles.",
    },
    "pt-BR": {
      title: "Mapa Interativo de Heartopia — Todos os Locais | Heartopia.guide",
      description:
        "Explore o mapa completo da ilha de Heartopia: todos os locais, áreas de pesca, NPCs e lojas, com filtros de pontos de pesca, coleta e colecionáveis.",
    },
    es: {
      title: "Mapa Interactivo de Heartopia — Todas las Ubicaciones | Heartopia.guide",
      description:
        "Explora el mapa completo de la isla de Heartopia: todas las ubicaciones, zonas de pesca, NPC y tiendas, con filtros de pesca, recolección y coleccionables.",
    },
  },
  "/tracker": {
    en: {
      title: "Heartopia Collection Tracker — Fish, Insects, Birds & More | Heartopia.guide",
      description:
        "Track your Heartopia collection: mark fish, insects, birds, crops, flowers, forageables, recipes and animals as collected. Progress saves in your browser.",
    },
    "pt-BR": {
      title: "Rastreador de Coleção de Heartopia — Peixes, Insetos, Pássaros e Mais | Heartopia.guide",
      description:
        "Acompanhe sua coleção de Heartopia: marque peixes, insetos, pássaros, cultivos, flores, coletáveis, receitas e animais. O progresso fica salvo no navegador.",
    },
    es: {
      title: "Rastreador de Colección de Heartopia — Peces, Insectos, Aves y Más | Heartopia.guide",
      description:
        "Sigue tu colección de Heartopia: marca peces, insectos, aves, cultivos, flores, recolectables, recetas y animales. El progreso se guarda en tu navegador.",
    },
  },
  "/items": {
    en: {
      title: "All Heartopia Items — Full Catalog | Heartopia.guide",
      description:
        "Browse the complete Heartopia item catalog: fish, insects, birds, crops, flowers, dishes, tools, furniture and more, with sell prices and sources.",
    },
    "pt-BR": {
      title: "Todos os Itens de Heartopia — Catálogo Completo | Heartopia.guide",
      description:
        "Navegue pelo catálogo completo de itens de Heartopia: peixes, insetos, pássaros, cultivos, flores, pratos, ferramentas, móveis e mais, com preços e fontes.",
    },
    es: {
      title: "Todos los Objetos de Heartopia — Catálogo Completo | Heartopia.guide",
      description:
        "Explora el catálogo completo de objetos de Heartopia: peces, insectos, aves, cultivos, flores, platos, herramientas, muebles y más, con precios y fuentes.",
    },
  },
  "/locations": {
    en: {
      title: "All Heartopia Locations | Heartopia.guide",
      description:
        "Every location in Heartopia: what you can fish, catch and forage in each area, plus the NPCs and shops you'll find there.",
    },
    "pt-BR": {
      title: "Todos os Locais de Heartopia | Heartopia.guide",
      description:
        "Todos os locais de Heartopia: o que dá para pescar, capturar e coletar em cada área, além dos NPCs e lojas de cada uma.",
    },
    es: {
      title: "Todas las Ubicaciones de Heartopia | Heartopia.guide",
      description:
        "Todas las ubicaciones de Heartopia: qué puedes pescar, atrapar y recolectar en cada zona, además de los NPC y tiendas de cada una.",
    },
  },
  "/npcs": {
    en: {
      title: "All Heartopia NPCs — Roles, Shops & Schedules | Heartopia.guide",
      description:
        "Meet every Heartopia NPC: their role on the island, which shop they run, and where to find them day to day.",
    },
    "pt-BR": {
      title: "Todos os NPCs de Heartopia — Funções, Lojas e Rotinas | Heartopia.guide",
      description:
        "Conheça todos os NPCs de Heartopia: a função de cada um na ilha, qual loja administram e onde encontrá-los no dia a dia.",
    },
    es: {
      title: "Todos los NPC de Heartopia — Funciones, Tiendas y Rutinas | Heartopia.guide",
      description:
        "Conoce a todos los NPC de Heartopia: su función en la isla, qué tienda administran y dónde encontrarlos día a día.",
    },
  },
  "/shops": {
    en: {
      title: "All Heartopia Shops & What They Sell | Heartopia.guide",
      description: "Every shop in Heartopia with its full stock: what each store sells, prices, and which NPC runs it.",
    },
    "pt-BR": {
      title: "Todas as Lojas de Heartopia e o Que Vendem | Heartopia.guide",
      description:
        "Todas as lojas de Heartopia com o estoque completo: o que cada uma vende, preços e qual NPC administra.",
    },
    es: {
      title: "Todas las Tiendas de Heartopia y Qué Venden | Heartopia.guide",
      description:
        "Todas las tiendas de Heartopia con su inventario completo: qué vende cada una, precios y qué NPC la administra.",
    },
  },
  // Browse tables. `/furniture` is deliberately absent: it doubles as the `furniture` ItemKind
  // index, so GENERATED_INDEX_COPY already covers it. These two have no kind behind them, and
  // without an entry here they get no PageMeta, no prerendered file, and answer 404 on a direct
  // visit — which is exactly what /wardrobe did between its launch and 2026-08-15.
  "/wardrobe": {
    en: {
      title: "Every Outfit & Accessory in Heartopia | Heartopia.guide",
      description:
        "All 2,239 clothing items in Heartopia — hats, tops, bottoms, shoes, bags and hair — filterable by slot, collection and colour, with prices and every design variant.",
    },
    "pt-BR": {
      title: "Todas as Roupas e Acessórios de Heartopia | Heartopia.guide",
      description:
        "Todas as 2.239 peças de roupa de Heartopia — chapéus, blusas, calças, sapatos, bolsas e cabelos — filtráveis por tipo, coleção e cor, com preços e todas as variantes.",
    },
    es: {
      title: "Toda la Ropa y Accesorios de Heartopia | Heartopia.guide",
      description:
        "Las 2.239 prendas de Heartopia — sombreros, tops, pantalones, zapatos, bolsos y peinados — filtrables por tipo, colección y color, con precios y todas las variantes.",
    },
  },
  "/buildings": {
    en: {
      title: "Every Building Piece in Heartopia — Build Mode | Heartopia.guide",
      description:
        "All 452 build-mode pieces in Heartopia — doors, windows, roofs, fences, wall sculptures and wall or floor textures — grouped by build style, with prices.",
    },
    "pt-BR": {
      title: "Todas as Peças de Construção de Heartopia | Heartopia.guide",
      description:
        "Todas as 452 peças do modo construção de Heartopia — portas, janelas, telhados, cercas, esculturas de parede e texturas — agrupadas por estilo, com preços.",
    },
    es: {
      title: "Todas las Piezas de Construcción de Heartopia | Heartopia.guide",
      description:
        "Las 452 piezas del modo construcción de Heartopia — puertas, ventanas, tejados, vallas, esculturas de pared y texturas — agrupadas por estilo, con precios.",
    },
  },
  "/guides": {
    en: {
      title: "Heartopia Guides — Fishing, Cooking, Pets & Leveling | Heartopia.guide",
      description:
        "Beginner and advanced Heartopia guides: fishing, gardening, cooking, birdwatching, insect catching, pet care and fast leveling strategies.",
    },
    "pt-BR": {
      title: "Guias de Heartopia — Pesca, Culinária, Pets e Evolução | Heartopia.guide",
      description:
        "Guias de Heartopia para iniciantes e veteranos: pesca, jardinagem, culinária, observação de pássaros, captura de insetos, pets e evolução rápida.",
    },
    es: {
      title: "Guías de Heartopia — Pesca, Cocina, Mascotas y Niveles | Heartopia.guide",
      description:
        "Guías de Heartopia para principiantes y expertos: pesca, jardinería, cocina, observación de aves, captura de insectos, mascotas y subida rápida de nivel.",
    },
  },
  "/achievements": {
    en: {
      title: "Heartopia Achievements List | Heartopia.guide",
      description:
        "All Heartopia achievements and how to complete them — requirements, rewards and tips for finishing your achievement log.",
    },
    "pt-BR": {
      title: "Lista de Conquistas de Heartopia | Heartopia.guide",
      description:
        "Todas as conquistas de Heartopia e como completá-las — requisitos, recompensas e dicas para fechar seu registro de conquistas.",
    },
    es: {
      title: "Lista de Logros de Heartopia | Heartopia.guide",
      description:
        "Todos los logros de Heartopia y cómo completarlos: requisitos, recompensas y consejos para terminar tu registro de logros.",
    },
  },
  "/weather": {
    en: {
      title: "Heartopia Weather Guide — What Each Weather Unlocks | Heartopia.guide",
      description:
        "How weather works in Heartopia: which fish, insects and events depend on rain, snow and other conditions, and how to plan around the forecast.",
    },
    "pt-BR": {
      title: "Guia de Clima de Heartopia — O Que Cada Clima Libera | Heartopia.guide",
      description:
        "Como o clima funciona em Heartopia: quais peixes, insetos e eventos dependem de chuva, neve e outras condições, e como planejar com a previsão.",
    },
    es: {
      title: "Guía del Clima de Heartopia — Qué Desbloquea Cada Clima | Heartopia.guide",
      description:
        "Cómo funciona el clima en Heartopia: qué peces, insectos y eventos dependen de la lluvia, la nieve y otras condiciones, y cómo planificar con el pronóstico.",
    },
  },
  "/events": {
    en: {
      title: "Heartopia Events Calendar — Current & Past Events | Heartopia.guide",
      description:
        "Every Heartopia event: current and past seasonal events, exclusive items and how to earn event rewards before they rotate out.",
    },
    "pt-BR": {
      title: "Calendário de Eventos de Heartopia — Atuais e Passados | Heartopia.guide",
      description:
        "Todos os eventos de Heartopia: eventos sazonais atuais e passados, itens exclusivos e como ganhar as recompensas antes que saiam de rotação.",
    },
    es: {
      title: "Calendario de Eventos de Heartopia — Actuales y Pasados | Heartopia.guide",
      description:
        "Todos los eventos de Heartopia: eventos de temporada actuales y pasados, objetos exclusivos y cómo conseguir las recompensas antes de que roten.",
    },
  },
  "/pets": {
    en: {
      title: "Heartopia Pets — Dogs, Cats, Traits & Care | Heartopia.guide",
      description:
        "Everything about Heartopia pets: all dog and cat breeds, pet traits, and how to adopt, feed and care for your companions.",
    },
    "pt-BR": {
      title: "Pets de Heartopia — Cães, Gatos, Traços e Cuidados | Heartopia.guide",
      description:
        "Tudo sobre os pets de Heartopia: todas as raças de cães e gatos, traços de personalidade e como adotar, alimentar e cuidar dos seus companheiros.",
    },
    es: {
      title: "Mascotas de Heartopia — Perros, Gatos, Rasgos y Cuidados | Heartopia.guide",
      description:
        "Todo sobre las mascotas de Heartopia: todas las razas de perros y gatos, rasgos de personalidad y cómo adoptar, alimentar y cuidar a tus compañeros.",
    },
  },
  "/pets/dogs": {
    en: {
      title: "All Dog Breeds in Heartopia | Heartopia.guide",
      description:
        "Every dog breed you can adopt in Heartopia, with appearance, traits and adoption details for each one.",
    },
    "pt-BR": {
      title: "Todas as Raças de Cães em Heartopia | Heartopia.guide",
      description:
        "Todas as raças de cães que você pode adotar em Heartopia, com aparência, traços e detalhes de adoção de cada uma.",
    },
    es: {
      title: "Todas las Razas de Perros en Heartopia | Heartopia.guide",
      description:
        "Todas las razas de perros que puedes adoptar en Heartopia, con apariencia, rasgos y detalles de adopción de cada una.",
    },
  },
  "/pets/cats": {
    en: {
      title: "All Cat Breeds in Heartopia | Heartopia.guide",
      description:
        "Every cat breed you can adopt in Heartopia, with appearance, traits and adoption details for each one.",
    },
    "pt-BR": {
      title: "Todas as Raças de Gatos em Heartopia | Heartopia.guide",
      description:
        "Todas as raças de gatos que você pode adotar em Heartopia, com aparência, traços e detalhes de adoção de cada uma.",
    },
    es: {
      title: "Todas las Razas de Gatos en Heartopia | Heartopia.guide",
      description:
        "Todas las razas de gatos que puedes adoptar en Heartopia, con apariencia, rasgos y detalles de adopción de cada una.",
    },
  },
  "/pets/traits": {
    en: {
      title: "Heartopia Pet Traits Explained | Heartopia.guide",
      description:
        "All pet personality traits in Heartopia and what they do — how traits affect your pet's behavior and which combinations to look for.",
    },
    "pt-BR": {
      title: "Traços de Pets de Heartopia Explicados | Heartopia.guide",
      description:
        "Todos os traços de personalidade dos pets de Heartopia e o que fazem — como afetam o comportamento e quais combinações procurar.",
    },
    es: {
      title: "Rasgos de Mascotas de Heartopia Explicados | Heartopia.guide",
      description:
        "Todos los rasgos de personalidad de las mascotas de Heartopia y qué hacen: cómo afectan su comportamiento y qué combinaciones buscar.",
    },
  },
  "/pets/guide": {
    en: {
      title: "Heartopia Pet Care Guide | Heartopia.guide",
      description:
        "How to care for pets in Heartopia: adoption, feeding, happiness, walks and everything else your dog or cat needs day to day.",
    },
    "pt-BR": {
      title: "Guia de Cuidados com Pets de Heartopia | Heartopia.guide",
      description:
        "Como cuidar dos pets em Heartopia: adoção, alimentação, felicidade, passeios e tudo o que seu cão ou gato precisa no dia a dia.",
    },
    es: {
      title: "Guía de Cuidado de Mascotas de Heartopia | Heartopia.guide",
      description:
        "Cómo cuidar a las mascotas en Heartopia: adopción, alimentación, felicidad, paseos y todo lo que tu perro o gato necesita a diario.",
    },
  },
  "/daily-checklist": {
    en: {
      title: "Heartopia Daily Checklist — Never Miss a Task | Heartopia.guide",
      description:
        "A daily checklist for Heartopia: recurring tasks, resets and routines to make the most of every in-game day.",
    },
    "pt-BR": {
      title: "Checklist Diário de Heartopia — Não Perca Nenhuma Tarefa | Heartopia.guide",
      description:
        "Um checklist diário para Heartopia: tarefas recorrentes, resets e rotinas para aproveitar ao máximo cada dia no jogo.",
    },
    es: {
      title: "Lista Diaria de Heartopia — No Te Pierdas Ninguna Tarea | Heartopia.guide",
      description:
        "Una lista diaria para Heartopia: tareas recurrentes, reinicios y rutinas para aprovechar al máximo cada día en el juego.",
    },
  },
  "/codes": {
    en: {
      title: "Heartopia Gift Codes — Active Codes & Rewards | Heartopia.guide",
      description:
        "All active Heartopia gift codes and their rewards, plus how to redeem them — checked and updated regularly.",
    },
    "pt-BR": {
      title: "Códigos de Presente de Heartopia — Códigos Ativos e Recompensas | Heartopia.guide",
      description:
        "Todos os códigos de presente ativos de Heartopia e suas recompensas, além de como resgatá-los — conferidos e atualizados regularmente.",
    },
    es: {
      title: "Códigos de Regalo de Heartopia — Códigos Activos y Recompensas | Heartopia.guide",
      description:
        "Todos los códigos de regalo activos de Heartopia y sus recompensas, además de cómo canjearlos: verificados y actualizados regularmente.",
    },
  },
  "/tracking-winter-season": {
    en: {
      title: "Heartopia Winter Season Tracker | Heartopia.guide",
      description:
        "Track Heartopia's winter season: seasonal fish, insects, event items and everything you can only collect while winter lasts.",
    },
    "pt-BR": {
      title: "Rastreador da Temporada de Inverno de Heartopia | Heartopia.guide",
      description:
        "Acompanhe a temporada de inverno de Heartopia: peixes e insetos sazonais, itens de evento e tudo o que só dá para coletar enquanto o inverno durar.",
    },
    es: {
      title: "Rastreador de la Temporada de Invierno de Heartopia | Heartopia.guide",
      description:
        "Sigue la temporada de invierno de Heartopia: peces e insectos de temporada, objetos de evento y todo lo que solo puedes conseguir mientras dure el invierno.",
    },
  },
  "/support": {
    en: {
      title: "Support Heartopia.guide | Heartopia.guide",
      description:
        "Ways to support Heartopia.guide — a free, player-made field guide — and help keep it updated for everyone.",
    },
    "pt-BR": {
      title: "Apoie o Heartopia.guide | Heartopia.guide",
      description:
        "Formas de apoiar o Heartopia.guide — um guia gratuito feito por jogadores — e ajudar a mantê-lo atualizado para todo mundo.",
    },
    es: {
      title: "Apoya a Heartopia.guide | Heartopia.guide",
      description:
        "Formas de apoyar a Heartopia.guide — una guía gratuita hecha por jugadores — y ayudar a mantenerla actualizada para todos.",
    },
  },
  // The footer links /sitemap from every page on the site, but the route had no PageMeta entry, so
  // generate-static-meta.ts wrote no file for it and it answered a real 404 — a sitewide internal
  // link to a dead URL. It worked when reached by clicking inside the app, which is exactly how
  // this class of bug hides (see the /wardrobe note in CLAUDE.md).
  "/sitemap": {
    en: {
      title: "Site Map — Every Page on Heartopia.guide | Heartopia.guide",
      description:
        "Browse every section of Heartopia.guide in one place: fish, insects, birds, flowers, recipes, NPCs, locations, guides and news.",
    },
    "pt-BR": {
      title: "Mapa do Site — Todas as Páginas do Heartopia.guide | Heartopia.guide",
      description:
        "Navegue por todas as seções do Heartopia.guide em um só lugar: peixes, insetos, aves, flores, receitas, NPCs, locais, guias e notícias.",
    },
    es: {
      title: "Mapa del Sitio — Todas las Páginas de Heartopia.guide | Heartopia.guide",
      description:
        "Explora todas las secciones de Heartopia.guide en un solo lugar: peces, insectos, aves, flores, recetas, NPC, ubicaciones, guías y noticias.",
    },
  },
  "/news": {
    en: {
      title: "Heartopia News — Updates, Events & Patch Coverage | Heartopia.guide",
      description:
        "The latest Heartopia news: game updates, seasonal events, new content and patch coverage, in one place.",
    },
    "pt-BR": {
      title: "Notícias de Heartopia — Atualizações, Eventos e Patches | Heartopia.guide",
      description:
        "As últimas notícias de Heartopia: atualizações do jogo, eventos sazonais, novos conteúdos e cobertura de patches, tudo em um só lugar.",
    },
    es: {
      title: "Noticias de Heartopia — Actualizaciones, Eventos y Parches | Heartopia.guide",
      description:
        "Las últimas noticias de Heartopia: actualizaciones del juego, eventos de temporada, contenido nuevo y cobertura de parches, todo en un solo lugar.",
    },
  },
};

export const STATIC_PAGE_COPY = Object.fromEntries(
  Object.entries(STATIC_PAGE_COPY_BASE).map(([path, copy]) => [
    path,
    {
      ...copy,
      id: (STATIC_PAGE_COPY_ID as Record<string, { title: string; description: string }>)[path],
      th: (STATIC_PAGE_COPY_TH as Record<string, { title: string; description: string }>)[path],
      de: (STATIC_PAGE_COPY_DE as Record<string, { title: string; description: string }>)[path],
    },
  ]),
) as Record<string, LocaleCopy>;

const GENERATED_INDEX_COPY: Record<string, LocaleCopy> = generatedIndexCopy();
